import { Skeleton } from '@sr/ui-library';
import { Fragment, ReactNode } from 'react';
import { EntityList } from './EntityList';
import { SkeletonRegion } from './SkeletonRegion';

interface SnapshotListSkeletonProps {
  /** Adds a placeholder for the section heading, for lists whose header loads with the cards. */
  hasTitle?: boolean;
  /**
   * What is loading, e.g. "Loading drafts". Home mounts three of these at once,
   * so each needs its own name for screen readers and for `getByRole('status')`.
   */
  label: string;
  numCards: number;
  /**
   * Renders the skeleton for a single card at the given index, e.g.
   * `(index) => <TaskCardSkeleton type={type} />`. Each result is keyed
   * here, so callers do not. Kept as a render prop so this stays agnostic to
   * what kind of card it repeats.
   */
  renderCard: (index: number) => ReactNode;
}

/**
 * Placeholder for a list of cards while its query resolves, shared by the Home
 * sections and the Tasks page. Cards are the real card shell, so their height
 * follows the markup rather than a hand-computed number that can silently fall
 * out of step.
 */
export const SnapshotListSkeleton = ({
  hasTitle,
  label,
  numCards,
  renderCard,
}: SnapshotListSkeletonProps) => (
  <SkeletonRegion label={label}>
    <EntityList>
      {hasTitle && <Skeleton className="h-6 w-48" />}

      <EntityList.Content>
        {Array.from({ length: numCards }).map((_, index) => (
          <Fragment key={index}>{renderCard(index)}</Fragment>
        ))}
      </EntityList.Content>
    </EntityList>
  </SkeletonRegion>
);
